import { useState, useCallback } from 'react'
import { message, Modal } from '@surecompanies/core_components'
import { VirtualCardType } from 'modules/virtual-cards/constants'
import { normalizeVcnProvider } from 'modules/virtual-cards/normalizeVcnProvider'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  getVirtualCardsForEmployee,
  createWexVirtualCardWithDetails,
  createMarqetaVirtualCard,
  reuseWexVirtualCardWithDetails,
  reuseMarqetaVirtualCard,
  performWexCardAction,
  performMarqetaCardAction,
  terminateAndCleanupCards,
  type AvailableVcnCard,
} from 'enrollment-platform/api/vcn'
import type { VcnAction, VirtualCardData } from '../VcnPaymentManagement.types'
import {
  VcnCardAction,
  VCN_CARD_ACTION_LABELS,
} from '../VcnPaymentManagement.types'

// Re-export AvailableVcnCard type for consumers
export type { AvailableVcnCard }

interface UseVcnActionsOptions {
  benefitId: string
  employeePublicKey?: string
  personFullName?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

interface GenerateVcnOptions {
  reason?: string
  reasonCategory?: string
  zendeskTicket?: string
}

interface ReuseVcnOptions {
  reason?: string
  reasonCategory?: string
  zendeskTicket?: string
}

interface UseVcnActionsReturn {
  // Generate VCN
  generateVcn: (
    provider: VirtualCardType,
    reason: string,
    options?: GenerateVcnOptions
  ) => Promise<void>
  isGenerating: boolean

  // Generate VCN with terminate all (for flag1 flow)
  generateVcnWithTerminateAll: (
    provider: VirtualCardType,
    options: GenerateVcnOptions
  ) => Promise<void>
  isTerminatingAll: boolean

  // Reuse VCN
  reuseVcn: (card: AvailableVcnCard, options?: ReuseVcnOptions) => Promise<void>
  isReusing: boolean

  // VCN Actions (suspend, terminate, reactivate)
  performAction: (
    action: VcnAction,
    card: VirtualCardData,
    reason?: string
  ) => Promise<void>
  isPerformingAction: boolean

  // Available cards for reuse
  availableCards: AvailableVcnCard[]
  isLoadingAvailableCards: boolean
  loadAvailableCards: () => Promise<void>
}

export function useVcnActions({
  benefitId,
  employeePublicKey,
  personFullName,
  onSuccess,
  onError,
}: UseVcnActionsOptions): UseVcnActionsReturn {
  const { flags } = useFeatureFlags()
  const [isGenerating, setIsGenerating] = useState(false)
  const [isTerminatingAll, setIsTerminatingAll] = useState(false)
  const [isReusing, setIsReusing] = useState(false)
  const [isPerformingAction, setIsPerformingAction] = useState(false)
  const [isLoadingAvailableCards, setIsLoadingAvailableCards] = useState(false)
  const [availableCards, setAvailableCards] = useState<AvailableVcnCard[]>([])

  const loadAvailableCards = useCallback(async () => {
    if (!employeePublicKey) return
    setIsLoadingAvailableCards(true)
    try {
      const cards = await getVirtualCardsForEmployee(employeePublicKey)
      setAvailableCards(cards)
    } catch (error) {
      console.error('Failed to load available cards:', error)
      setAvailableCards([])
    } finally {
      setIsLoadingAvailableCards(false)
    }
  }, [employeePublicKey])

  const generateVcn = useCallback(
    async (
      provider: VirtualCardType,
      reason: string,
      options?: GenerateVcnOptions
    ) => {
      if (!benefitId) {
        message.error('Benefit ID is required')
        return
      }

      setIsGenerating(true)
      try {
        if (provider === VirtualCardType.WEX) {
          await createWexVirtualCardWithDetails(benefitId)
        } else {
          await createMarqetaVirtualCard(benefitId)
        }

        postActivityLog({
          action: ActivityLogAction.GENERATE_VCN,
          description: `Generated VCN (${provider}) for ${personFullName ?? 'unknown'}`,
          targetUserId: employeePublicKey,
          additionalData: {
            benefitId,
            provider,
            reason,
            reasonCategory: options?.reasonCategory,
            zendeskTicket: options?.zendeskTicket,
          },
        })

        message.success('VCN generated successfully')
        onSuccess?.()
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          'Failed to generate VCN'
        message.error(errorMessage)
        onError?.(error)
      } finally {
        setIsGenerating(false)
      }
    },
    [benefitId, onSuccess, onError, employeePublicKey, personFullName]
  )

  const generateVcnWithTerminateAll = useCallback(
    async (provider: VirtualCardType, options: GenerateVcnOptions) => {
      if (!benefitId) {
        message.error('Benefit ID is required')
        return
      }

      setIsTerminatingAll(true)
      try {
        // Step 1: Terminate and cleanup all existing cards (note is passed for audit)
        const cleanupResponse = await terminateAndCleanupCards({
          benefitId,
          note: options.reason,
        })

        // Step 2: Generate new VCN and capture response
        setIsGenerating(true)
        let generateResponse: Record<string, unknown> | null = null
        if (provider === VirtualCardType.WEX) {
          const wexResponse = await createWexVirtualCardWithDetails(benefitId)
          generateResponse = {
            cardId: wexResponse.id,
            pan: wexResponse.pan
              ? `****${wexResponse.pan.slice(-4)}`
              : undefined,
            state: wexResponse.state,
            vcnProvider: VirtualCardType.WEX,
            merchantLogId: wexResponse.merchantLogId,
          }
        } else {
          const marqetaResponse = await createMarqetaVirtualCard(benefitId)
          generateResponse = {
            cardId:
              marqetaResponse.VirtualCard?.card_public_key ??
              marqetaResponse.VirtualCard?.token,
            pan: marqetaResponse.VirtualCard?.pan
              ? `****${marqetaResponse.VirtualCard.pan.slice(-4)}`
              : undefined,
            state: marqetaResponse.VirtualCard?.state,
            vcnProvider: VirtualCardType.MARQETA,
          }
        }

        // Check if any cards were actually terminated
        const terminatedCount = cleanupResponse?.results?.length ?? 0
        const didTerminateCards = terminatedCount > 0

        // Step 3: Log activity with responses (only include cleanup data if cards were terminated)
        postActivityLog({
          action: ActivityLogAction.GENERATE_VCN,
          description: didTerminateCards
            ? `Generated VCN (${provider}) for ${personFullName ?? 'unknown'} after terminating ${terminatedCount} existing card(s)`
            : `Generated VCN (${provider}) for ${personFullName ?? 'unknown'}`,
          targetUserId: employeePublicKey,
          additionalData: {
            benefitId,
            provider,
            reason: options.reason,
            reasonCategory: options.reasonCategory,
            zendeskTicket: options.zendeskTicket,
            // Only include terminatedAllCards and cleanupResponse if cards were actually terminated
            ...(didTerminateCards && {
              terminatedAllCards: true,
              cleanupResponse: {
                terminatedCount,
                skippedCount: cleanupResponse?.skipped?.length ?? 0,
                results: cleanupResponse?.results,
                skipped: cleanupResponse?.skipped,
              },
            }),
            generateResponse,
          },
        })

        message.success('VCN generated successfully')
        onSuccess?.()
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          'Failed to generate VCN'
        message.error(errorMessage)
        onError?.(error)
      } finally {
        setIsTerminatingAll(false)
        setIsGenerating(false)
      }
    },
    [benefitId, employeePublicKey, onSuccess, onError, personFullName]
  )

  const reuseVcn = useCallback(
    async (card: AvailableVcnCard, options?: ReuseVcnOptions) => {
      if (!benefitId || !employeePublicKey) {
        message.error('Benefit ID and Employee ID are required')
        return
      }

      setIsReusing(true)
      try {
        if (card.virtualCardType === VirtualCardType.WEX) {
          if (!card.merchantLogId) {
            throw new Error('WEX card requires merchantLogId')
          }
          await reuseWexVirtualCardWithDetails(card.merchantLogId, benefitId)
        } else {
          await reuseMarqetaVirtualCard({
            benefitId,
            cardId: card.userPublicKey,
            cardPublicKey: card.id,
            userPublicKey: employeePublicKey,
          })
        }

        postActivityLog({
          action: ActivityLogAction.REUSE_VCN,
          description: `Reused VCN for ${personFullName ?? 'unknown'}`,
          targetUserId: employeePublicKey,
          additionalData: {
            benefitId,
            cardId: card.userPublicKey,
            cardPublicKey: card.id,
            virtualCardType: card.virtualCardType,
            merchantLogId: card.merchantLogId,
            ...(options?.reason && { reason: options.reason }),
            ...(options?.reasonCategory && {
              reasonCategory: options.reasonCategory,
            }),
            ...(options?.zendeskTicket && {
              zendeskTicket: options.zendeskTicket,
            }),
          },
        })

        message.success('VCN reused successfully')
        onSuccess?.()
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ??
          error?.message ??
          'Failed to reuse VCN'
        message.error(errorMessage)
        onError?.(error)
      } finally {
        setIsReusing(false)
      }
    },
    [benefitId, employeePublicKey, onSuccess, onError, personFullName]
  )

  const performAction = useCallback(
    async (action: VcnAction, card: VirtualCardData, _reason?: string) => {
      if (!benefitId) {
        message.error('Benefit ID is required')
        return
      }

      // Generate action is handled separately
      if (action === 'generate') {
        return
      }

      // Type narrowing: action is now VcnCardAction
      const cardAction = action as VcnCardAction

      const provider = normalizeVcnProvider(card.vcnProvider)
      const cardToken = card.token ?? card.merchantLogId

      if (!cardToken) {
        message.error('Card token is required')
        return
      }

      const cardName =
        card.firstName && card.lastName
          ? `${card.firstName} ${card.lastName}`
          : (card.cardholderName ?? personFullName ?? 'unknown')

      const executeAction = async ({
        reason,
        reasonCategory,
        zendeskTicket,
      }: {
        reason?: string
        reasonCategory?: string
        zendeskTicket?: string
      }) => {
        setIsPerformingAction(true)
        try {
          if (provider === VirtualCardType.WEX) {
            await performWexCardAction(cardAction, cardToken, benefitId)
          } else {
            // Marqeta card actions via VCN service (direct API call)
            await performMarqetaCardAction({
              action: cardAction,
              cardPublicKey: card.card_public_key ?? cardToken,
              reason: reason ?? 'User Action',
              sourceUserId: employeePublicKey,
              benefitId,
            })
          }

          postActivityLog({
            action: ActivityLogAction.SUBMIT_VCN_ACTION,
            description: `Submitted VCN action (${cardAction}) for ${cardName}`,
            targetUserId: employeePublicKey,
            additionalData: {
              action: cardAction,
              benefitId,
              provider,
              cardToken,
              reason,
              reasonCategory,
              zendeskTicket,
            },
          })

          message.success(`VCN ${cardAction} successful`)
          onSuccess?.()
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ??
            error?.message ??
            `Failed to ${cardAction} VCN`
          message.error(errorMessage)
          onError?.(error)
        } finally {
          setIsPerformingAction(false)
        }
      }

      // Use activity log popup for VCN actions (suspend, terminate, reactivate)
      const isActivityLogPopupEnabled = Boolean(
        flags['admin.activity-log-reason-popup-enabled']
      )

      const actionLabel = VCN_CARD_ACTION_LABELS[cardAction]
      const capitalizedLabel =
        actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)

      if (isActivityLogPopupEnabled) {
        // Import store and action to dispatch activity log popup
        const storeModule = await import('store/createStore')
        const { openActivityLogPopup } = await import(
          'modules/activity-log-service/activityLogPopupSlice'
        )
        const store = storeModule.storeInstance as {
          dispatch: (action: unknown) => void
        } | null
        if (store) {
          store.dispatch(
            openActivityLogPopup({
              contentText: `Are you sure you want to ${actionLabel} VCN ${cardName}?`,
              onSubmit: executeAction,
              required: true,
            })
          )
        } else {
          // Fallback to simple confirmation if store not available
          Modal.confirm({
            title: `${capitalizedLabel} VCN?`,
            content: `Are you sure you want to ${actionLabel} VCN ${cardName}?`,
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => executeAction({}),
          })
        }
      } else {
        // Show simple confirmation modal when activity log popup is disabled
        Modal.confirm({
          title: `${capitalizedLabel} VCN?`,
          content: `Are you sure you want to ${actionLabel} VCN ${cardName}?`,
          okText: 'Confirm',
          cancelText: 'Cancel',
          onOk: () => executeAction({}),
        })
      }
    },
    [benefitId, onSuccess, onError, employeePublicKey, personFullName, flags]
  )

  return {
    generateVcn,
    isGenerating,
    generateVcnWithTerminateAll,
    isTerminatingAll,
    reuseVcn,
    isReusing,
    performAction,
    isPerformingAction,
    availableCards,
    isLoadingAvailableCards,
    loadAvailableCards,
  }
}
